import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  PlayArrow as PlayArrowIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";

interface CommandArgument {
  type: "string" | "number" | "boolean" | "array" | "select" | "file" | "timestamp";
  description: string;
  required: boolean;
  example?: any;
  options?: Array<{ value: any; label: string }>;
  multiline?: boolean;
}

interface CommandDefinition {
  key: string;
  name: string;
  description: string;
  arguments: Record<string, CommandArgument>;
}

interface ExecuteCommandResponse {
  success: boolean;
  result?: any;
  error?: string;
  logs?: string[];
}

export function AdminCommandsPage() {
  const [commands, setCommands] = useState<CommandDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<string>("");
  const [commandArgs, setCommandArgs] = useState<Record<string, any>>({});
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] =
    useState<ExecuteCommandResponse | null>(null);
  const [executionLogs, setExecutionLogs] = useState<string[]>([]);

  useEffect(() => {
    fetchCommands();
  }, []);

  const fetchCommands = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<{ commands: CommandDefinition[] }>(
        "/admin/commands/list"
      );
      setCommands(response.commands);
    } catch (err: any) {
      console.error("Error fetching commands:", err);
      setError(err.message || "Failed to load commands");
    } finally {
      setLoading(false);
    }
  };

  const handleCommandSelect = (commandKey: string) => {
    setSelectedCommand(commandKey);
    setExecutionResult(null);
    setExecutionLogs([]);

    // Initialize arguments with example values
    const command = commands.find((cmd) => cmd.key === commandKey);
    if (command) {
      const initialArgs: Record<string, any> = {};
      Object.entries(command.arguments).forEach(([argName, argDef]) => {
        if (argDef.example !== undefined) {
          // For timestamp fields with null example, use current date
          if (argDef.type === "timestamp" && argDef.example === null) {
            initialArgs[argName] = Date.now();
          } else if (Array.isArray(argDef.example)) {
            initialArgs[argName] = argDef.example.join(", ");
          } else {
            initialArgs[argName] = argDef.example;
          }
        }
      });
      setCommandArgs(initialArgs);
    }
  };

  const handleArgumentChange = (argName: string, value: string | number) => {
    setCommandArgs((prev) => ({
      ...prev,
      [argName]: value,
    }));
  };

  const processArgumentValue = (
    argName: string,
    value: any,
    argDef: CommandArgument
  ) => {
    switch (argDef.type) {
      case "array":
        return typeof value === "string"
          ? value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s.length > 0)
          : value;
      case "number":
        return typeof value === "string" ? parseFloat(value) : value;
      case "boolean":
        return typeof value === "string"
          ? value.toLowerCase() === "true"
          : Boolean(value);
      case "timestamp":
        // Value is already a timestamp number from the DateTimePicker
        return typeof value === "number" ? value : parseInt(value);
      case "select":
        return value; // Select values are already properly typed
      default:
        return value;
    }
  };

  const executeCommand = async () => {
    if (!selectedCommand) return;

    try {
      setExecuting(true);

      const command = commands.find((cmd) => cmd.key === selectedCommand);
      if (!command) return;

      // Check if we have any file arguments
      const hasFileArgs = Object.values(command.arguments).some(
        (arg) => arg.type === "file"
      );

      if (hasFileArgs) {
        // Use FormData for file uploads
        const formData = new FormData();
        formData.append("command", selectedCommand);

        // Process arguments, handling files specially
        const processedArgs: Record<string, any> = {};
        Object.entries(commandArgs).forEach(([argName, value]) => {
          const argDef = command.arguments[argName];
          if (argDef) {
            if (argDef.type === "file" && value instanceof File) {
              formData.append(argName, value);
            } else {
              processedArgs[argName] = processArgumentValue(
                argName,
                value,
                argDef
              );
            }
          }
        });

        // Add non-file arguments to FormData
        formData.append("arguments", JSON.stringify(processedArgs));

        console.log("Executing command with files:", selectedCommand);

        const result = await apiClient.fetch<ExecuteCommandResponse>(
          "/admin/commands/execute",
          {
            method: "POST",
            body: formData,
            headers: {
              // Don't set Content-Type, let the browser set it for FormData
            },
          }
        );
        setExecutionResult(result);
        if (result.logs) {
          setExecutionLogs(result.logs);
        }

        console.log("Command execution result:", result);
      } else {
        // Use regular JSON for non-file commands
        const processedArgs: Record<string, any> = {};
        Object.entries(commandArgs).forEach(([argName, value]) => {
          const argDef = command.arguments[argName];
          if (argDef) {
            processedArgs[argName] = processArgumentValue(
              argName,
              value,
              argDef
            );
          }
        });

        console.log(
          "Executing command:",
          selectedCommand,
          "with args:",
          processedArgs
        );

        const response = await apiClient.post<ExecuteCommandResponse>(
          "/admin/commands/execute",
          {
            command: selectedCommand,
            arguments: processedArgs,
          }
        );

        setExecutionResult(response);
        if (response.logs) {
          setExecutionLogs(response.logs);
        }

        console.log("Command execution result:", response);
      }
    } catch (err: any) {
      console.error("Command execution error:", err);
      setExecutionResult({
        success: false,
        error: err.message || "Command execution failed",
      });
    } finally {
      setExecuting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatArgumentExample = (argDef: CommandArgument): string => {
    if (argDef.example === undefined) return "";
    if (Array.isArray(argDef.example)) {
      return argDef.example.join(", ");
    }
    return String(argDef.example);
  };

  if (loading) {
    return (
      <AdminPage>
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "40px" }}
        >
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Box sx={{ padding: "20px" }}>
        <Typography variant="h4" sx={{ marginBottom: "20px" }}>
          Commands
        </Typography>

        {error && (
          <Alert severity="error" sx={{ marginBottom: "20px" }}>
            {error}
          </Alert>
        )}

        {/* Command Selection */}
        <Card sx={{ marginBottom: "20px" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Available Commands
            </Typography>

            <FormControl fullWidth sx={{ marginBottom: "20px" }}>
              <InputLabel>Select Command</InputLabel>
              <Select
                value={selectedCommand}
                onChange={(e) => handleCommandSelect(e.target.value)}
                label="Select Command"
              >
                <MenuItem value="">
                  <em>Choose a command...</em>
                </MenuItem>
                {commands.map((command) => (
                  <MenuItem key={command.key} value={command.key}>
                    {command.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Command Details */}
            {selectedCommand && (
              <Box>
                {commands
                  .filter((cmd) => cmd.key === selectedCommand)
                  .map((command) => (
                    <Box key={command.key}>
                      <Typography variant="subtitle1" gutterBottom>
                        <strong>{command.name}</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {command.description}
                      </Typography>

                      {/* Arguments */}
                      <Typography
                        variant="h6"
                        sx={{ marginTop: "20px", marginBottom: "10px" }}
                      >
                        Arguments
                      </Typography>

                      <Stack spacing={2}>
                        {Object.entries(command.arguments).map(
                          ([argName, argDef]) => (
                            <Box key={argName}>
                              {argDef.type === "timestamp" ? (
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                  <DateTimePicker
                                    label={argName}
                                    value={
                                      commandArgs[argName]
                                        ? new Date(Number(commandArgs[argName]))
                                        : null
                                    }
                                    onChange={(newValue: Date | null) => {
                                      if (newValue) {
                                        handleArgumentChange(
                                          argName,
                                          newValue.getTime()
                                        );
                                      }
                                    }}
                                    slotProps={{
                                      textField: {
                                        fullWidth: true,
                                        required: argDef.required,
                                        helperText: (
                                          <Box>
                                            <Typography
                                              variant="caption"
                                              display="block"
                                            >
                                              {argDef.description}
                                            </Typography>
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              sx={{ marginTop: "4px" }}
                                            >
                                              <Chip
                                                label={argDef.type}
                                                size="small"
                                                variant="outlined"
                                              />
                                              <Chip
                                                label={
                                                  argDef.required
                                                    ? "Required"
                                                    : "Optional"
                                                }
                                                size="small"
                                                color={
                                                  argDef.required
                                                    ? "error"
                                                    : "default"
                                                }
                                                variant="outlined"
                                              />
                                              {argDef.example && (
                                                <Typography
                                                  variant="caption"
                                                  color="text.secondary"
                                                >
                                                  Example:{" "}
                                                  {new Date(
                                                    argDef.example
                                                  ).toLocaleString()}
                                                </Typography>
                                              )}
                                            </Stack>
                                          </Box>
                                        ),
                                      },
                                    }}
                                  />
                                </LocalizationProvider>
                              ) : argDef.type === "file" ? (
                                <Box>
                                  <input
                                    type="file"
                                    id={`file-${argName}`}
                                    style={{ display: "none" }}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        handleArgumentChange(
                                          argName,
                                          file as any
                                        );
                                      }
                                    }}
                                  />
                                  <label htmlFor={`file-${argName}`}>
                                    <Button
                                      variant="outlined"
                                      component="span"
                                      fullWidth
                                      sx={{
                                        justifyContent: "flex-start",
                                        textAlign: "left",
                                      }}
                                    >
                                      {commandArgs[argName] instanceof File
                                        ? (commandArgs[argName] as File).name
                                        : "Choose File..."}
                                    </Button>
                                  </label>
                                  <Box sx={{ marginTop: "4px" }}>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      {argDef.description}
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ marginTop: "4px" }}
                                    >
                                      <Chip
                                        label={argDef.type}
                                        size="small"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={
                                          argDef.required
                                            ? "Required"
                                            : "Optional"
                                        }
                                        size="small"
                                        color={
                                          argDef.required ? "error" : "default"
                                        }
                                        variant="outlined"
                                      />
                                    </Stack>
                                  </Box>
                                </Box>
                              ) : argDef.type === "boolean" ? (
                                <Box>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={
                                          commandArgs[argName] === true ||
                                          commandArgs[argName] === "true"
                                        }
                                        onChange={(e) =>
                                          handleArgumentChange(
                                            argName,
                                            e.target.checked.toString()
                                          )
                                        }
                                      />
                                    }
                                    label={argName}
                                  />
                                  <Box sx={{ marginTop: "4px" }}>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      {argDef.description}
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ marginTop: "4px" }}
                                    >
                                      <Chip
                                        label={argDef.type}
                                        size="small"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={
                                          argDef.required
                                            ? "Required"
                                            : "Optional"
                                        }
                                        size="small"
                                        color={
                                          argDef.required ? "error" : "default"
                                        }
                                        variant="outlined"
                                      />
                                      {argDef.example !== undefined && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Default: {String(argDef.example)}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Box>
                                </Box>
                              ) : argDef.type === "select" ? (
                                <FormControl
                                  fullWidth
                                  required={argDef.required}
                                >
                                  <InputLabel>{argName}</InputLabel>
                                  <Select
                                    value={commandArgs[argName] || ""}
                                    onChange={(e) =>
                                      handleArgumentChange(
                                        argName,
                                        e.target.value as string
                                      )
                                    }
                                    label={argName}
                                  >
                                    {!argDef.required && (
                                      <MenuItem value="">
                                        <em>None</em>
                                      </MenuItem>
                                    )}
                                    {argDef.options?.map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  <Box sx={{ marginTop: "4px" }}>
                                    <Typography
                                      variant="caption"
                                      display="block"
                                      color="text.secondary"
                                    >
                                      {argDef.description}
                                    </Typography>
                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      sx={{ marginTop: "4px" }}
                                    >
                                      <Chip
                                        label={argDef.type}
                                        size="small"
                                        variant="outlined"
                                      />
                                      <Chip
                                        label={
                                          argDef.required
                                            ? "Required"
                                            : "Optional"
                                        }
                                        size="small"
                                        color={
                                          argDef.required ? "error" : "default"
                                        }
                                        variant="outlined"
                                      />
                                    </Stack>
                                  </Box>
                                </FormControl>
                              ) : (
                                <TextField
                                  fullWidth
                                  label={argName}
                                  value={commandArgs[argName] || ""}
                                  onChange={(e) =>
                                    handleArgumentChange(
                                      argName,
                                      e.target.value
                                    )
                                  }
                                  placeholder={formatArgumentExample(argDef)}
                                  helperText={
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        display="block"
                                      >
                                        {argDef.description}
                                      </Typography>
                                      <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                        sx={{ marginTop: "4px" }}
                                      >
                                        <Chip
                                          label={argDef.type}
                                          size="small"
                                          variant="outlined"
                                        />
                                        <Chip
                                          label={
                                            argDef.required
                                              ? "Required"
                                              : "Optional"
                                          }
                                          size="small"
                                          color={
                                            argDef.required
                                              ? "error"
                                              : "default"
                                          }
                                          variant="outlined"
                                        />
                                        {argDef.example && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            Example:{" "}
                                            {formatArgumentExample(argDef)}
                                          </Typography>
                                        )}
                                      </Stack>
                                    </Box>
                                  }
                                  required={argDef.required}
                                  multiline={
                                    argDef.type === "array" ||
                                    argDef.multiline === true
                                  }
                                  minRows={
                                    argDef.type === "array" ||
                                    argDef.multiline === true
                                      ? 3
                                      : 1
                                  }
                                  maxRows={
                                    argDef.type === "array" ||
                                    argDef.multiline === true
                                      ? 15
                                      : 1
                                  }
                                />
                              )}
                            </Box>
                          )
                        )}
                      </Stack>

                      {/* Execute Button */}
                      <Box sx={{ marginTop: "20px" }}>
                        <Button
                          variant="contained"
                          onClick={executeCommand}
                          disabled={executing}
                          startIcon={
                            executing ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PlayArrowIcon />
                            )
                          }
                        >
                          {executing ? "Executing..." : "Execute Command"}
                        </Button>
                      </Box>
                    </Box>
                  ))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Execution Results */}
        {executionResult && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Execution Results
              </Typography>

              <Alert
                severity={executionResult.success ? "success" : "error"}
                sx={{ marginBottom: "16px" }}
              >
                {executionResult.success
                  ? "Command executed successfully"
                  : "Command execution failed"}
                {executionResult.error && `: ${executionResult.error}`}
              </Alert>

              {/* Logs - Always shown */}
              {executionLogs.length > 0 && (
                <Box sx={{ marginBottom: "16px" }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Execution Logs ({executionLogs.length} entries)
                  </Typography>
                  <Paper sx={{ padding: "12px", backgroundColor: "#f5f5f5" }}>
                    {executionLogs.map((log, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        component="div"
                        sx={{ fontFamily: "monospace", marginBottom: "4px" }}
                      >
                        {log}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}

              {/* Result Data - Always shown if present */}
              {executionResult.result && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Result Data
                  </Typography>
                  <Box sx={{ position: "relative" }}>
                    <Tooltip title="Copy to clipboard">
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyToClipboard(
                            JSON.stringify(executionResult.result, null, 2)
                          )
                        }
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 1,
                        }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Paper sx={{ padding: "12px", backgroundColor: "#f5f5f5" }}>
                      <pre
                        style={{
                          margin: 0,
                          overflow: "auto",
                          fontSize: "12px",
                          fontFamily: "monospace",
                        }}
                      >
                        {JSON.stringify(executionResult.result, null, 2)}
                      </pre>
                    </Paper>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </AdminPage>
  );
}
